import React from "react";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { translationLocale } from "@ui/i18n";

export const useExternalLinks = () => {
  const rawExternalLinks = useQuery("/externalLinks").body;

  const getCachedExternalLinks = useGetQueryData("/externalLinks");
  const fetchUpdatedItems = React.useCallback(async (since: string) => {
    const { externalLinks, deletedExternalLinkIds } = (
      await call("GET", "/externalLinks/updates", {
        query: { since: new Date(since) },
      })
    ).body;
    return [externalLinks, deletedExternalLinkIds] as const;
  }, []);

  useChannelUpdates(
    { key: "external_links" },
    {
      readItems: React.useCallback(() => getCachedExternalLinks()?.body, [getCachedExternalLinks]),
      fetchUpdatedItems,
      mutateItems: useMutateBody("/externalLinks"),
    },
  );

  return rawExternalLinks;
};

export const useLocalExternalLink = () => {
  const externalLinks = useExternalLinks();

  return React.useMemo(
    () =>
      externalLinks.map(externalLink => ({
        ...externalLink,
        localName: translationLocale === "fr" ? externalLink.frName : externalLink.enName,
      })),
    [externalLinks],
  );
};

export type ExternalLink = ReturnType<typeof useExternalLinks>[number];
export type LocalExternalLink = ReturnType<typeof useLocalExternalLink>[number];
